import React, { useState, useCallback, useEffect } from 'react';
import Button from '@/components/ui/Button';

interface RemarksModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (remark: string) => void;
    pendingStatusChange: {
        leadUuid: string;
        fromStatus: string;
        toStatus: string;
        lead: any;
    } | null;
    statusColumns: Array<{
        uuid: string;
        name: string;
    }>;
    isProcessing?: boolean;
}

const RemarksModal: React.FC<RemarksModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    pendingStatusChange,
    statusColumns,
    isProcessing = false
}) => {
    const [remark, setRemark] = useState('');

    // Reset remark when modal opens
    useEffect(() => {
        if (isOpen) {
            setRemark('');
        }
    }, [isOpen]);

    // Optimized change handler - only updates local state
    const handleRemarkChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setRemark(e.target.value);
    }, []);

    const handleSubmit = useCallback(() => {
        if (remark.trim()) {
            onSubmit(remark.trim());
        }
    }, [remark, onSubmit]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSubmit();
        }
    }, [handleSubmit]);

    if (!isOpen || !pendingStatusChange) return null;

    const fromStatusName = statusColumns.find(s => s.uuid === pendingStatusChange.fromStatus)?.name || 'Unknown';
    const toStatusName = statusColumns.find(s => s.uuid === pendingStatusChange.toStatus)?.name || 'Unknown';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-semibold mb-4">Add Remark for Status Change</h3>
                <div className="space-y-4">
                    <div>
                        <p className="text-sm text-gray-600 mb-2">
                            Moving lead <strong>{pendingStatusChange.lead.phone}</strong> from{' '}
                            <strong>{fromStatusName}</strong> to{' '}
                            <strong>{toStatusName}</strong>
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Remark (Required)
                        </label>
                        <textarea
                            value={remark}
                            onChange={handleRemarkChange}
                            onKeyDown={handleKeyDown}
                            rows={3}
                            placeholder="Enter reason for status change... (Ctrl+Enter to submit)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            autoComplete="off"
                            spellCheck={false}
                            autoFocus
                            disabled={isProcessing}
                        />
                    </div>
                </div>
                <div className="flex gap-3 mt-6">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="flex-1"
                        disabled={isProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!remark.trim() || isProcessing}
                        className="flex-1"
                    >
                        {isProcessing ? 'Updating...' : 'Update Status'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(RemarksModal);